import { z } from "zod";

import { CreateUserDTORole } from "@/common/api";
import { locale } from "@/common/locale";
import { PASSWORD_ALLOWED_CHARS_REGEX } from "@/common/model";

// Схема валидации пароля
export const passwordSchema = z
  .string()
  .regex(
    PASSWORD_ALLOWED_CHARS_REGEX,
    "Пароль может содержать только латинские буквы, цифры и специальные символы",
  )
  .min(8, "Минимальная длина пароля - 8 символов")
  .max(64, "Максимальная длина пароля - 64 символов")
  .refine(
    (password) => /[A-Z]/.test(password),
    "Пароль должен содержать латиницу верхнего регистра (A–Z)",
  )
  .refine(
    (password) => /[a-z]/.test(password),
    "Пароль должен содержать латиницу нижнего регистра (a–z)",
  )
  .refine((password) => /[0-9]/.test(password), "Пароль должен содержать цифры (0–9)")
  .refine(
    (password) => /[@$&!%*?^_+\-=\[\]{}()|\\:";'<>.,\/#~`]/.test(password),
    "Пароль должен содержать специальные символы (@, $, &, и др.)",
  );

const passwordOptionalSchema = z.preprocess(
  (value) => (value === "" ? undefined : value),
  passwordSchema.optional(),
);

// Базовая схема формы пользователя (всё кроме пароля)
export const userFormBaseSchema = z.object({
  // Данные пользователя
  login: z.string().min(1, locale.required).max(30, locale.maxLength(30)),
  username: z.string().min(3, locale.minLength(3)),
  role: z.nativeEnum(CreateUserDTORole),
  contactNumber: z.string().min(10, locale.phoneMinLength),
  email: z.string().min(1, locale.required).email(locale.invalidEmailFormat),

  // Данные компании
  companyName: z.string().optional(),
  inn: z.string().regex(/^\d{10}$|^\d{12}$/, locale.innFormat),
  kpp: z
    .string()
    .min(1, locale.required)
    .regex(/^\d{9}$/, locale.kppFormat),
  ogrn: z
    .string()
    .min(1, locale.required)
    .regex(/^\d{13}$/, locale.ogrnFormat),
  companyEmail: z.string().email(locale.invalidEmailFormat).optional().or(z.literal("")),
  companyContactNumber: z.string().min(10, locale.phoneMinLength),
  addressLegal: z.string().min(1, locale.required),
  addressActual: z.string().min(1, locale.required),
});

export const createUserSchema = userFormBaseSchema.extend({
  passwordHash: passwordSchema,
});

export const editUserSchema = userFormBaseSchema.extend({
  passwordHash: passwordOptionalSchema,
});

// Для обратной совместимости: ранее в UI использовалась `userFormSchema`
export const userFormSchema = createUserSchema;

export type CreateUserData = z.infer<typeof createUserSchema>;
export type EditUserData = z.infer<typeof editUserSchema>;

// Общий тип для формы в UI (подходит и для create, и для edit)
export type UserFormData = EditUserData;
